import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { AnnotationMetadata } from '@/features/annotation'
import { ResponseThread } from '@/schema'

import { ECommentStatus, TCommentStatus } from '../types'

interface DeletingThread {
  threadId: string
  documentVersionId: string
}

interface CommentStoreState {
  commentsByDocVersion: Record<string, Record<string, ResponseThread>>
  annotationsByDocVersion: Record<string, Record<string, AnnotationMetadata>>
  activeCommentId: string | undefined
  activeDocumentVersionId: string | undefined
  showCommentDetail: boolean
  commentStatus: TCommentStatus
  selectedCommentId: string | null
  deleteDialogOpen: boolean
  deletingThread: DeletingThread | null
  showCommentAvatarsByDoc: Record<string, boolean>
  editingComment: ResponseThread | null
  quotedCommentId: string | null
  targetReplyId: string | null
}

interface CommentStoreActions {
  setComments(docVersionId: string, comments: ResponseThread[]): void

  addComment(docVersionId: string, comment: ResponseThread): void

  addReply(docVersionId: string, reply: ResponseThread): void

  removeComment(docVersionId: string, commentId: string): void

  setAnnotation(
    docVersionId: string,
    commentId: string,
    anchor: AnnotationMetadata
  ): void

  setActiveCommentId(commentId: string | undefined): void

  setShowCommentDetail(show: boolean): void

  setActiveDocumentVersionId(docVersionId: string | undefined): void

  getCommentsForDoc(docVersionId: string): Record<string, ResponseThread>

  getAnnotationsForDoc(docVersionId: string): Record<string, AnnotationMetadata>

  getCommentById(
    commentId: string,
    docVersionId: string
  ): ResponseThread | undefined

  getAnnotationByCommentId(
    commentId: string,
    docVersionId: string
  ): AnnotationMetadata | undefined

  getThreadComments(
    threadId: string,
    docVersionId: string
  ): {
    root: ResponseThread | null
    replies: ResponseThread[]
  }

  updateCommentReadStatus(docVersionId: string, commentId: string): void

  clearAllComments(): void

  setCommentStatus: (status: TCommentStatus) => void
  setSelectedCommentId: (commentId: string | null) => void
  setDeleteDialogOpen: (open: boolean) => void
  setDeletingThread: (thread: DeletingThread | null) => void
  clearDeletingThread: () => void
  setShowCommentAvatars: (documentId: string, show: boolean) => void
  clearDocumentState: (documentId: string) => void
  setEditingComment: (comment: ResponseThread | null) => void
  setQuotedComment: (commentId: string | null) => void
  setTargetReplyId: (replyId: string | null) => void
  reset: () => void
}

type CommentStore = CommentStoreState & CommentStoreActions

const commentStoreInitialState: CommentStoreState = {
  commentsByDocVersion: {},
  annotationsByDocVersion: {},
  activeCommentId: undefined,
  activeDocumentVersionId: undefined,
  showCommentDetail: false,
  commentStatus: ECommentStatus.ALL,
  selectedCommentId: null,
  deleteDialogOpen: false,
  deletingThread: null,
  showCommentAvatarsByDoc: {},
  editingComment: null,
  quotedCommentId: null,
  targetReplyId: null,
}

export const useCommentStore = create<CommentStore>()(
  devtools(
    (set, get) => ({
      ...commentStoreInitialState,

      setComments: (docVersionId: string, comments: ResponseThread[]) => {
        const commentsById = comments.reduce<Record<string, ResponseThread>>(
          (acc, c) => {
            acc[c.id] = c
            return acc
          },
          {}
        )

        set(
          (state) => ({
            commentsByDocVersion: {
              ...state.commentsByDocVersion,
              [docVersionId]: commentsById,
            },
          }),
          false,
          'setComments'
        )
      },

      addComment: (docVersionId: string, comment: ResponseThread) => {
        set(
          (state) => {
            const existing = state.commentsByDocVersion[docVersionId] ?? {}
            return {
              commentsByDocVersion: {
                ...state.commentsByDocVersion,
                [docVersionId]: {
                  ...existing,
                  [comment.id]: comment,
                },
              },
            }
          },
          false,
          'addComment'
        )
      },

      addReply: (docVersionId: string, reply: ResponseThread) => {
        set(
          (state) => {
            const existing = state.commentsByDocVersion[docVersionId] ?? {}
            return {
              commentsByDocVersion: {
                ...state.commentsByDocVersion,
                [docVersionId]: {
                  ...existing,
                  [reply.id]: reply,
                },
              },
            }
          },
          false,
          'addReply'
        )
      },

      removeComment: (docVersionId: string, commentId: string) => {
        set(
          (state) => {
            const commentsForDoc =
              state.commentsByDocVersion[docVersionId] ?? {}
            const { [commentId]: _, ...restComments } = commentsForDoc

            const annotationsForDoc =
              state.annotationsByDocVersion[docVersionId] ?? {}
            const { [commentId]: __, ...restAnnotations } = annotationsForDoc

            return {
              commentsByDocVersion: {
                ...state.commentsByDocVersion,
                [docVersionId]: restComments,
              },
              annotationsByDocVersion: {
                ...state.annotationsByDocVersion,
                [docVersionId]: restAnnotations,
              },
              activeCommentId:
                state.activeCommentId === commentId
                  ? undefined
                  : state.activeCommentId,
            }
          },
          false,
          'removeComment'
        )
      },

      setAnnotation: (
        docVersionId: string,
        commentId: string,
        anchor: AnnotationMetadata
      ) => {
        set(
          (state) => {
            const annotationsForDoc =
              state.annotationsByDocVersion[docVersionId] ?? {}

            return {
              annotationsByDocVersion: {
                ...state.annotationsByDocVersion,
                [docVersionId]: {
                  ...annotationsForDoc,
                  [commentId]: anchor,
                },
              },
            }
          },
          false,
          'setAnnotation'
        )
      },

      setActiveCommentId: (commentId: string | undefined) => {
        set({ activeCommentId: commentId }, false, 'setActiveCommentId')
      },

      setShowCommentDetail: (show: boolean) => {
        set({ showCommentDetail: show }, false, 'setShowCommentDetail')
      },

      setActiveDocumentVersionId: (docVersionId: string | undefined) => {
        set(
          { activeDocumentVersionId: docVersionId },
          false,
          'setActiveDocumentVersionId'
        )
      },

      getCommentsForDoc: (docVersionId: string) => {
        return get().commentsByDocVersion[docVersionId] ?? {}
      },

      getAnnotationsForDoc: (docVersionId: string) => {
        return get().annotationsByDocVersion[docVersionId] ?? {}
      },

      getCommentById: (commentId: string, docVersionId: string) => {
        return get().commentsByDocVersion[docVersionId]?.[commentId]
      },

      getAnnotationByCommentId: (commentId: string, docVersionId: string) => {
        return get().annotationsByDocVersion[docVersionId]?.[commentId]
      },

      getThreadComments: (threadId: string, docVersionId: string) => {
        const comments = get().commentsByDocVersion[docVersionId] ?? {}
        const allComments = Object.values(comments)

        const root =
          allComments.find(
            (c) =>
              c.id === threadId ||
              (c.thread_id === threadId && !c.parent_comment_id)
          ) || null

        const replies = allComments.filter(
          (c) =>
            c.thread_id === threadId &&
            c.parent_comment_id === threadId &&
            c.id !== threadId
        )

        return { root, replies }
      },

      updateCommentReadStatus: (docVersionId: string, commentId: string) => {
        set(
          (state) => {
            const commentsForDoc =
              state.commentsByDocVersion[docVersionId] ?? {}
            const comment = commentsForDoc[commentId]

            if (!comment) return state

            return {
              commentsByDocVersion: {
                ...state.commentsByDocVersion,
                [docVersionId]: {
                  ...commentsForDoc,
                  [commentId]: {
                    ...comment,
                    has_unread_replies: false,
                  },
                },
              },
            }
          },
          false,
          'updateCommentReadStatus'
        )
      },

      clearAllComments: () => {
        set(commentStoreInitialState, false, 'clearAllComments')
      },

      setCommentStatus: (status: TCommentStatus) => {
        set({ commentStatus: status }, false, 'setCommentStatus')
      },

      setSelectedCommentId: (commentId: string | null) => {
        set({ selectedCommentId: commentId }, false, 'setSelectedCommentId')
      },

      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      setDeletingThread: (thread: DeletingThread | null) => {
        set({ deletingThread: thread }, false, 'setDeletingThread')
      },

      clearDeletingThread: () => {
        set({ deletingThread: null }, false, 'clearDeletingThread')
      },

      setShowCommentAvatars: (documentId: string, show: boolean) => {
        set(
          (state) => ({
            showCommentAvatarsByDoc: {
              ...state.showCommentAvatarsByDoc,
              [documentId]: show,
            },
          }),
          false,
          'setShowCommentAvatars'
        )
      },

      clearDocumentState: (documentId: string) => {
        set(
          (state) => {
            const newShowCommentAvatarsByDoc = {
              ...state.showCommentAvatarsByDoc,
            }
            delete newShowCommentAvatarsByDoc[documentId]

            return {
              showCommentAvatarsByDoc: newShowCommentAvatarsByDoc,
            }
          },
          false,
          'clearDocumentState'
        )
      },

      setEditingComment: (comment: ResponseThread | null) => {
        set({ editingComment: comment }, false, 'setEditingComment')
      },

      setQuotedComment: (commentId: string | null) => {
        set({ quotedCommentId: commentId }, false, 'setQuotedComment')
      },

      setTargetReplyId: (replyId: string | null) => {
        set({ targetReplyId: replyId }, false, 'setTargetReplyId')
      },

      reset: () => {
        set(commentStoreInitialState, false, 'reset')
      },
    }),
    {
      name: 'CommentStore',
    }
  )
)
